import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { CheckCircle, XCircle, Broadcast } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Panel de Integraciones: SMM API + Meta Pixel.
 */
export const IntegrationsPanel = ({ initialSmm = {}, initialTracking = { meta_pixel_id: '', meta_capi_access_token: '', active: true }, onSaved }) => {
  const [apiUrl, setApiUrl] = useState(initialSmm.api_url || '');
  const [apiKey, setApiKey] = useState(initialSmm.api_key || '');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const [pixelId, setPixelId] = useState(initialTracking.meta_pixel_id || '');
  const [capiToken, setCapiToken] = useState(initialTracking.meta_capi_access_token || '');
  const [capiConfigured, setCapiConfigured] = useState(!!initialTracking.capi_configured);
  const [trackingActive, setTrackingActive] = useState(initialTracking.active !== false);

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/smm-api/test`,
        { smm_api_url: apiUrl, smm_api_key: apiKey },
        { withCredentials: true }
      );
      setTestResult(data);
      if (data.ok) toast.success(`Conexión OK · ${data.latency_ms}ms · ${data.services_count || '?'} servicios`);
      else toast.error(data.error || 'Falló la prueba');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error probando conexión';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      setTestResult({ ok: false, error: msg });
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/settings`,
        { smm_api_url: apiUrl, smm_api_key: apiKey },
        { withCredentials: true }
      );
      toast.success('Configuración SMM guardada');
      onSaved && onSaved();
    } catch (e) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const savePixel = async () => {
    const raw = pixelId.trim();
    if (raw && !/^\d+$/.test(raw)) {
      toast.error('El Pixel ID debe ser numérico');
      return;
    }
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/tracking`,
        { meta_pixel_id: raw, meta_capi_access_token: capiToken.trim() },
        { withCredentials: true }
      );
      setCapiConfigured(!!data.capi_configured);
      toast.success('Meta Pixel + CAPI guardado');
    } catch (e) {
      const m = e?.response?.data?.detail || 'Error al guardar';
      toast.error(typeof m === 'string' ? m : JSON.stringify(m));
    }
  };

  const togglePixel = async () => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/tracking`,
        { active: !trackingActive },
        { withCredentials: true }
      );
      setTrackingActive(data.active !== false);
      toast.success(data.active ? 'Tracking activado' : 'Tracking desactivado');
    } catch (e) {
      toast.error('Error');
    }
  };

  return (
    <div className="space-y-6" data-testid="integrations-panel">
      <h2 className="text-2xl font-bold flex items-center gap-2">
        <Broadcast size={20} weight="duotone" className="text-primary" /> Integraciones
      </h2>

      {/* SMM API */}
      <Card className="p-6 border-border" data-testid="smm-api-card">
        <h3 className="text-lg font-semibold mb-1">Panel SMM</h3>
        <p className="text-sm text-muted-foreground mb-4">
          URL y KEY del proveedor SMM. Probá la conexión antes de guardar.
        </p>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="apiUrl">API URL</Label>
            <Input id="apiUrl" type="url" value={apiUrl} onChange={(e) => setApiUrl(e.target.value)} placeholder="https://mipanel.com/api/v2" data-testid="smm-api-url" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="apiKey">API Key</Label>
            <Input id="apiKey" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder="•••••••••••••" data-testid="smm-api-key" />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={test} disabled={testing || !apiUrl || !apiKey} data-testid="smm-test-button">
              {testing ? 'Probando...' : 'Probar conexión'}
            </Button>
            <Button onClick={save} disabled={saving} data-testid="smm-save-button">
              {saving ? 'Guardando...' : 'Guardar configuración'}
            </Button>
          </div>
          {testResult && (
            <div
              className={`text-sm p-3 rounded-lg flex items-start gap-2 ${
                testResult.ok ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'
              }`}
              data-testid="smm-test-result"
            >
              {testResult.ok ? <CheckCircle size={18} weight="fill" className="mt-0.5" /> : <XCircle size={18} weight="fill" className="mt-0.5" />}
              <div>
                {testResult.ok ? (
                  <>
                    <strong>Conexión OK</strong> · {testResult.latency_ms}ms
                    {typeof testResult.services_count === 'number' && (
                      <> · {testResult.services_count} servicios disponibles en el proveedor</>
                    )}
                    {testResult.balance_response && (
                      <div className="text-xs mt-1 font-mono">
                        Balance: {JSON.stringify(testResult.balance_response)}
                      </div>
                    )}
                  </>
                ) : (
                  <><strong>Error:</strong> {testResult.error}</>
                )}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Product Catalog Feed */}
      <Card className="p-6 border-border" data-testid="catalog-feed-card">
        <div className="mb-3">
          <h3 className="text-lg font-semibold">Catálogo de productos (Meta / Google / metamize)</h3>
          <p className="text-sm text-muted-foreground">
            Feed público auto-generado desde tus servicios activos. Pegá una de estas URLs en{' '}
            <strong>metamize.co</strong>, <strong>Meta Commerce Manager</strong> o{' '}
            <strong>Google Merchant</strong> como fuente del catálogo.
          </p>
        </div>
        <div className="space-y-2 text-sm">
          {[
            { fmt: 'XML (RSS + g:*)', path: '/api/catalog.xml', hint: 'Recomendado para Meta Catalog Feed' },
            { fmt: 'CSV (Meta Catalog)', path: '/api/catalog.csv', hint: 'Recomendado para metamize / uploads' },
            { fmt: 'JSON', path: '/api/catalog.json', hint: 'Para integraciones custom' },
          ].map((f) => {
            const url = `${window.location.origin}${f.path}`;
            return (
              <div key={f.path} className="flex items-center gap-2 flex-wrap p-3 rounded-lg bg-muted/40" data-testid={`catalog-url-${f.fmt}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{f.fmt}</div>
                  <div className="font-mono text-xs truncate">{url}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{f.hint}</div>
                </div>
                <div className="flex gap-1.5">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(url);
                      toast.success('Copiado');
                    }}
                    data-testid={`catalog-copy-${f.fmt}`}
                  >
                    Copiar
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(url, '_blank')}
                    data-testid={`catalog-open-${f.fmt}`}
                  >
                    Abrir
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Meta Pixel */}
      <Card className="p-6 border-border" data-testid="meta-pixel-card">        <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold">Meta Pixel (Facebook / Instagram Ads)</h3>
            <p className="text-sm text-muted-foreground">Eventos automáticos: PageView, ViewContent, AddToCart, Lead, InitiateCheckout, AddPaymentInfo, Purchase, CompleteRegistration, Contact.</p>
          </div>
          <div className={`text-sm font-semibold ${trackingActive ? 'text-emerald-500' : 'text-muted-foreground'}`}>
            {trackingActive ? '● Activo' : '○ Desactivado'}
          </div>
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pixelId">Pixel ID</Label>
            <Input id="pixelId" inputMode="numeric" pattern="[0-9]*" value={pixelId} onChange={(e) => setPixelId(e.target.value)} placeholder="Ej: 940049328402164" data-testid="meta-pixel-id-input" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Label htmlFor="capiToken">Conversions API — Access Token</Label>
              <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
                capiConfigured
                  ? 'bg-emerald-500/15 text-emerald-500'
                  : 'bg-yellow-500/15 text-yellow-500'
              }`}>
                {capiConfigured ? 'Configurado' : 'Sin configurar'}
              </span>
            </div>
            <Input
              id="capiToken"
              type="password"
              value={capiToken}
              onChange={(e) => setCapiToken(e.target.value)}
              placeholder="EAAG... (opcional — habilita eventos server-side)"
              data-testid="meta-capi-token-input"
              autoComplete="off"
            />
            <p className="text-[11px] text-muted-foreground leading-snug">
              Con el CAPI token configurado, cuando marques un pedido como completo desde Telegram
              (botón <span className="font-mono">🎯 Marcar completo (sin SMM)</span>) se dispara
              un evento <span className="font-mono">Purchase</span> server-side a Meta. Útil para
              que Meta reciba la conversión aunque el cliente no vuelva al sitio.
              Se obtiene en Meta Events Manager → tu pixel → Configuración → Generar token.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={savePixel} data-testid="meta-pixel-save">Guardar Pixel + CAPI</Button>
            <Button variant="outline" onClick={togglePixel} data-testid="meta-pixel-toggle">
              {trackingActive ? 'Desactivar tracking' : 'Activar tracking'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
